import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaPhoneAlt, FaTimes } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import PastillesCompetences from '../Espace/PastillesCompetences';
import { estPhotoPosee, typeImage } from '../../components/Utils/typeImage';

/**
 * La fiche d'un collaborateur (YRH-NEW-009-T2, Yros/Kavero#199), sortie de
 * l'annuaire par Yros/Kavero#488 pour s'ouvrir aussi depuis « Mon équipe ».
 *
 * Son responsable et ses N-1 y sont cliquables : on monte et on descend dans
 * l'organigramme sans quitter la fiche. `complement` y ajoute ce qu'un écran
 * sait de plus, comme la section du manager dans « Mon équipe ».
 *
 * Panneau qui monte du bas sur téléphone, à portée de pouce ; boîte centrée à
 * partir de `sm`.
 */

// Le vert se repère d'un coup d'œil ; le rouge reste le code demandé par
// l'équipe fonctionnelle mais atténué : sans pointage du jour, presque toute la
// grille est « absente » et ne doit pas ressembler à une page d'erreurs.
const PASTILLE_CLASSES = {
  present: 'bg-emerald-500',
  absent: 'bg-rose-300',
  en_conge: 'bg-slate-400',
};

// Au-delà, la bio se replie sur trois lignes avec « Voir plus ».
const BIO_REPLIEE = 160;

export const initiales = (nom) => nom.split(/\s+/).filter(Boolean).slice(0, 2)
  .map((mot) => mot[0].toUpperCase()).join('');

export const sousTitreDe = (collaborateur) =>
  [collaborateur.poste, collaborateur.departement].filter(Boolean).join(' · ');

export function Avatar({ collaborateur, taille = 'w-12 h-12', texte = 'text-base', pastille = 'w-3.5 h-3.5' }) {
  const { t } = useTranslation();
  const libelle = t(`annuaire.presence.${collaborateur.presence}`);
  // Sans photo posée, Odoo rend un avatar SVG générique : les initiales disent mieux qui c'est.
  const photo = estPhotoPosee(collaborateur.photo) ? collaborateur.photo : null;
  return (
    <span className={`relative shrink-0 ${taille}`}>
      {photo ? (
        <img src={`data:${typeImage(photo)};base64,${photo}`} alt=""
          className={`${taille} rounded-full object-cover`} />
      ) : (
        <span aria-hidden="true"
          className={`${taille} ${texte} rounded-full flex items-center justify-center bg-[--color-primary] text-white font-semibold`}>
          {initiales(collaborateur.nom)}
        </span>
      )}
      <span role="img" aria-label={libelle} title={libelle}
        className={`absolute bottom-0 right-0 ${pastille} rounded-full ring-2 ring-white ${PASTILLE_CLASSES[collaborateur.presence] || PASTILLE_CLASSES.absent}`} />
    </span>
  );
}

const PRESENCE_TEXTE = {
  present: 'text-emerald-700',
  absent: 'text-rose-600',
  en_conge: 'text-slate-600',
};

function Rubrique({ titre, children }) {
  return (
    <section>
      <h3 className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1.5">{titre}</h3>
      {children}
    </section>
  );
}

function Bio({ texte }) {
  const { t } = useTranslation();
  const [deplie, setDeplie] = useState(false);
  const longue = texte.length > BIO_REPLIEE;
  return (
    <>
      <p className={`text-sm text-slate-700 whitespace-pre-line break-words ${longue && !deplie ? 'line-clamp-3' : ''}`}>
        {texte}
      </p>
      {longue && (
        <button type="button" onClick={() => setDeplie(!deplie)}
          className="mt-1 text-sm text-[--color-primary] hover:underline">
          {t(deplie ? 'annuaire.fiche.voirMoins' : 'annuaire.fiche.voirPlus')}
        </button>
      )}
    </>
  );
}

const BOUTON_CONTACT =
  'inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium ' +
  'text-[--color-primary] hover:bg-slate-50 transition-colors';

function FicheCollaborateur({ employeId, onOuvrir, onFermer, complement = null }) {
  const { t } = useTranslation();
  const [fiche, setFiche] = useState(null);
  const [erreur, setErreur] = useState(false);
  const boite = useRef(null);
  const titreId = 'annuaire-fiche-titre';

  useEffect(() => {
    let actif = true;
    setFiche(null);
    setErreur(false);
    apiService.lireFicheAnnuaire(employeId)
      .then((reponse) => {
        if (!actif) return;
        if (reponse?.ok) setFiche(reponse.collaborateur);
        else setErreur(true);
      })
      .catch(() => { if (actif) setErreur(true); });
    return () => { actif = false; };
  }, [employeId]);

  // Le clavier entre dans la boîte : Échap la ferme.
  useEffect(() => { boite.current?.focus(); }, []);

  const sousTitre = fiche ? sousTitreDe(fiche) : '';
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-slate-900/40" onClick={onFermer}>
      <div ref={boite} role="dialog" aria-modal="true" aria-labelledby={titreId} tabIndex={-1}
        onClick={(evenement) => evenement.stopPropagation()}
        onKeyDown={(evenement) => { if (evenement.key === 'Escape') onFermer(); }}
        data-testid="fiche-collaborateur"
        className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[85vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-xl focus:outline-none"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <span aria-hidden="true" className="sm:hidden block mx-auto mt-2 w-10 h-1 rounded-full bg-slate-300" />
        <button type="button" onClick={onFermer} aria-label={t('annuaire.fiche.fermer')}
          className="absolute top-3 right-3 p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100">
          <FaTimes aria-hidden="true" />
        </button>

        <header className="flex items-center gap-4 p-6 pr-12">
          {fiche && <Avatar collaborateur={fiche} taille="w-16 h-16" texte="text-xl" pastille="w-4 h-4" />}
          <div className="min-w-0">
            <h2 id={titreId} className="text-lg font-semibold text-slate-900 break-words">
              {fiche?.nom || t('annuaire.fiche.chargement')}
            </h2>
            {sousTitre && <p className="text-sm text-slate-500">{sousTitre}</p>}
            {fiche && (
              <p className={`mt-1 text-sm font-medium ${PRESENCE_TEXTE[fiche.presence] || PRESENCE_TEXTE.absent}`}>
                {t(`annuaire.fiche.aujourdhui.${fiche.presence}`)}
              </p>
            )}
          </div>
        </header>

        {erreur && (
          <p role="alert" className="px-6 pb-6 text-sm text-red-700">{t('annuaire.fiche.erreur')}</p>
        )}
        {fiche && (
          <div className="px-6 pb-6 space-y-5">
            {(fiche.email || fiche.telephone) && (
              <div className="flex flex-wrap gap-2">
                {fiche.email && (
                  <a href={`mailto:${fiche.email}`} className={BOUTON_CONTACT} title={fiche.email}>
                    <FaEnvelope aria-hidden="true" />{t('annuaire.fiche.email')}
                  </a>
                )}
                {fiche.telephone && (
                  <a href={`tel:${fiche.telephone}`} className={BOUTON_CONTACT}>
                    <FaPhoneAlt aria-hidden="true" />{t('annuaire.fiche.appeler')}
                    <span className="font-normal text-slate-500">{fiche.telephone}</span>
                  </a>
                )}
              </div>
            )}
            {complement}
            {fiche.bio && (
              <Rubrique titre={t('annuaire.fiche.bio')}><Bio texte={fiche.bio} /></Rubrique>
            )}
            {fiche.langues.length > 0 && (
              <Rubrique titre={t('annuaire.fiche.langues')}>
                <ul className="flex flex-wrap gap-1.5">
                  {fiche.langues.map(({ langue, niveau }) => (
                    <li key={langue} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                      {`${langue} · ${niveau}`}
                    </li>
                  ))}
                </ul>
              </Rubrique>
            )}
            {fiche.competences?.length > 0 && (
              <Rubrique titre={t('annuaire.fiche.competences')}>
                <PastillesCompetences
                  lignes={fiche.competences.map((ligne) => ({ ...ligne, skill_id: ligne.competence }))} />
              </Rubrique>
            )}
            {fiche.responsable && (
              <Rubrique titre={t('annuaire.fiche.responsable')}>
                <button type="button" onClick={() => onOuvrir(fiche.responsable.id)}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors">
                  <span aria-hidden="true"
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-slate-100 text-slate-600 text-xs font-semibold">
                    {initiales(fiche.responsable.nom)}
                  </span>
                  <span className="text-sm font-medium text-slate-800">{fiche.responsable.nom}</span>
                </button>
              </Rubrique>
            )}
            {fiche.equipe?.length > 0 && (
              <Rubrique titre={t('annuaire.fiche.equipe', { count: fiche.equipe.length })}>
                <ul data-testid="fiche-equipe" className="flex flex-wrap gap-2">
                  {fiche.equipe.map((membre) => (
                    <li key={membre.id}>
                      <button type="button" onClick={() => onOuvrir(membre.id)}
                        className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-left hover:bg-slate-50 transition-colors">
                        <span aria-hidden="true"
                          className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center bg-slate-100 text-slate-600 text-xs font-semibold">
                          {initiales(membre.nom)}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-slate-800">{membre.nom}</span>
                          {membre.poste && <span className="block text-xs text-slate-500">{membre.poste}</span>}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Rubrique>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FicheCollaborateur;
